import { useEffect, useState } from "react";
import { Clock3, DollarSign, Loader2, ShieldAlert } from "lucide-react";
import { PartnerAPI } from "@/lib/partnerApi";

const STAGE_LABELS = {
    lapsed: "Lapsed",
    docs_prepared: "Docs prepared",
    fees_paid: "Fees paid",
    filing_submitted: "Filing submitted",
    under_review: "Under DCCA review",
    reinstated: "Reinstated · In good standing",
};

/**
 * EIGOO Entity Reinstatement + Pending Reserve ledger tile for the partner
 * Overview. Read-only for Ymaku; Director K drives it from /admin.
 */
export default function ReserveLedger() {
    const [data, setData] = useState(null);

    useEffect(() => {
        PartnerAPI.entityStatus()
            .then(setData)
            .catch(() => setData({ loadError: true }));
    }, []);

    if (!data) {
        return (
            <div className="py-10 text-center text-cream/55">
                <Loader2 size={22} className="animate-spin inline text-gold" />
            </div>
        );
    }
    if (data.loadError) return null;

    const stages = data.stages || [];
    const currentIdx = stages.indexOf(data.entity.status);
    const reinstated = data.reinstated;
    const balance = data.reserve.balance_dollars || 0;
    const totalAccrued = data.reserve.total_accrued_dollars || 0;

    return (
        <section data-testid="partner-reserve-ledger">
            <h2 className="font-serif text-3xl text-cream">
                EIGOO Reserve · Pending Reinstatement
            </h2>
            <p className="text-cream/60 text-sm mt-1 max-w-2xl leading-relaxed">
                On <strong className="text-cream">Lane 3 · EIGOO Dropship</strong>{" "}
                orders, EIGOO's 40% commission accrues to a reserve ledger
                until EIGOO Inc. is formally reinstated with the Hawai'i DCCA.
                Your 50% share and the CyberKids 10% royalty flow normally —
                this bar tracks what's waiting on the reinstatement.
            </p>

            <div className="mt-6 grid lg:grid-cols-3 gap-5">
                {/* Status card */}
                <div
                    className={`p-6 rounded-sm border backdrop-blur lg:col-span-2 ${
                        reinstated
                            ? "bg-cream/5 border-gold/60"
                            : "bg-brown/10 border-brown/40"
                    }`}
                    data-testid="partner-reserve-status-card"
                >
                    <div className="flex items-center gap-2">
                        <ShieldAlert
                            size={14}
                            className={reinstated ? "text-gold" : "text-cream/70"}
                        />
                        <span className="text-[0.62rem] tracking-[0.3em] uppercase text-cream/60">
                            EIGOO Inc. · {data.entity.jurisdiction}
                        </span>
                    </div>
                    <p
                        className={`font-serif text-3xl mt-2 ${
                            reinstated ? "text-gold" : "text-cream"
                        }`}
                        data-testid="partner-reserve-entity-status"
                    >
                        {STAGE_LABELS[data.entity.status] || data.entity.status}
                    </p>
                    {data.entity.notes && (
                        <p className="text-cream/60 text-sm mt-2">
                            {data.entity.notes}
                        </p>
                    )}

                    {/* Progress rail */}
                    <ol
                        className="mt-5 grid grid-cols-6 gap-1"
                        aria-label="Reinstatement progress"
                        data-testid="partner-reserve-progress-rail"
                    >
                        {stages.map((s, i) => {
                            const active = i <= currentIdx;
                            return (
                                <li key={s} className="flex flex-col gap-1.5">
                                    <span
                                        className={`block h-1.5 rounded-sm ${
                                            active ? "bg-gold" : "bg-cream/15"
                                        }`}
                                        aria-hidden
                                    />
                                    <span
                                        className={`text-[0.55rem] tracking-[0.15em] uppercase leading-tight ${
                                            active
                                                ? "text-cream"
                                                : "text-cream/35"
                                        }`}
                                    >
                                        {STAGE_LABELS[s]?.split(" · ")[0]}
                                    </span>
                                </li>
                            );
                        })}
                    </ol>

                    {data.entity.target_reinstatement_date && (
                        <p className="text-cream/55 text-xs mt-3 inline-flex items-center gap-1.5">
                            <Clock3 size={11} /> Target:{" "}
                            {data.entity.target_reinstatement_date}
                        </p>
                    )}
                </div>

                {/* Reserve balance card */}
                <div
                    className={`p-6 rounded-sm border backdrop-blur ${
                        reinstated
                            ? "bg-cream/5 border-cream/15"
                            : "bg-gold/10 border-gold"
                    }`}
                    data-testid="partner-reserve-balance-card"
                >
                    <div className="flex items-center gap-2">
                        <DollarSign size={14} className="text-gold" />
                        <span className="text-[0.62rem] tracking-[0.3em] uppercase text-cream/60">
                            Pending Reserve · EIGOO 40%
                        </span>
                    </div>
                    <p
                        className="font-serif text-5xl text-gold mt-2 leading-none"
                        data-testid="partner-reserve-balance"
                    >
                        ${balance.toFixed(2)}
                    </p>
                    <p className="text-cream/60 text-xs mt-1">
                        {data.reserve.entries_count} ledger{" "}
                        {data.reserve.entries_count === 1 ? "entry" : "entries"}{" "}
                        · ${totalAccrued.toFixed(2)} total accrued
                    </p>
                    <p className="text-cream/55 text-xs mt-4 leading-relaxed">
                        {reinstated
                            ? "EIGOO Inc. is reinstated. Distributions resume via normal monthly ACH."
                            : "Held until reinstatement clears. Nothing is owed to EIGOO by Ymaku personally — this is a bookkeeping reserve."}
                    </p>
                </div>
            </div>
        </section>
    );
}
